import { useEffect, useCallback } from 'react';
import { useTree } from '@/hooks/useTree';
import { useTreeStore } from '@/stores/treeStore';
import { TreeNode } from '@/types/tree';

interface UseTreeKeyboardNavigationProps {
  selectedNodeId: string | null;
  onSelect: (nodeId: string) => void;
  onExpand: (nodeId: string) => void;
  onCollapse: (nodeId: string) => void;
  /** Ctrl/Cmd+E — only handled when provided. */
  onEdit?: (nodeId: string) => void;
  /** F6 — reload selected node (context-menu reload). */
  onReload?: () => void;
  /** + or Ctrl/Cmd++ — add child (+ button / add menu). */
  onAdd?: () => void;
}

/** Main keyboard +, numpad +, Shift+=, or Ctrl/Cmd+= (US layouts). */
function isPlusKey(event: KeyboardEvent): boolean {
  return event.key === '+' || (event.key === '=' && (event.shiftKey || event.ctrlKey || event.metaKey));
}

/**
 * Document-level keyboard shortcuts for tree navigation (arrows, Enter, F6, +, Ctrl/Cmd+E).
 * Skips handling when focus is in an input or the detail pane.
 *
 * Expand/collapse follows `expandedNodes` (UI fold state), not `childrenLoaded` (fetch cache).
 */
export const useTreeKeyboardNavigation = ({
  selectedNodeId,
  onSelect,
  onExpand,
  onCollapse,
  onEdit,
  onReload,
  onAdd,
}: UseTreeKeyboardNavigationProps) => {
  const { areChildrenLoaded } = useTree();
  const getNodeData = useTreeStore((state) => state.getNodeData);
  const expandedNodes = useTreeStore((state) => state.expandedNodes);

  const isNodeExpanded = useCallback((nodeId: string) => expandedNodes.has(nodeId), [expandedNodes]);

  const canDescendIntoChildren = useCallback(
    (node: TreeNode) => Boolean(node.children?.length) && isNodeExpanded(node.nodeId) && areChildrenLoaded(node.nodeId),
    [areChildrenLoaded, isNodeExpanded]
  );

  /** Next row in visual tree order: expanded nodes go to their first child. */
  const firstVisibleInSubtree = useCallback(
    (nodeId: string): string => {
      const node = getNodeData(nodeId);
      if (node && canDescendIntoChildren(node)) {
        return node.children![0];
      }
      return nodeId;
    },
    [canDescendIntoChildren, getNodeData]
  );

  const getNextNode = useCallback(
    (currentNodeId: string): string | null => {
      const currentNode = getNodeData(currentNodeId);
      if (!currentNode) return null;

      if (canDescendIntoChildren(currentNode)) {
        return currentNode.children![0];
      }

      let node: TreeNode | null = currentNode;
      while (node?.parentId) {
        const parent = getNodeData(node.parentId);
        if (!parent?.children?.length) {
          node = parent ?? null;
          continue;
        }

        const currentIndex = parent.children.findIndex((child) => child === node!.nodeId);
        if (currentIndex >= 0 && currentIndex < parent.children.length - 1) {
          return firstVisibleInSubtree(parent.children[currentIndex + 1]);
        }

        node = parent;
      }

      return null;
    },
    [canDescendIntoChildren, firstVisibleInSubtree, getNodeData]
  );

  const getPreviousNode = useCallback(
    (currentNodeId: string): string | null => {
      const currentNode = getNodeData(currentNodeId);
      if (!currentNode) return null;

      const parentNode = currentNode.parentId ? getNodeData(currentNode.parentId) : null;
      if (parentNode?.children?.length) {
        const currentIndex = parentNode.children.findIndex((child) => child === currentNodeId);
        if (currentIndex > 0) {
          const previousSiblingId = parentNode.children[currentIndex - 1];
          const previousSibling = getNodeData(previousSiblingId);
          if (!previousSibling) return null;

          if (canDescendIntoChildren(previousSibling)) {
            const previousSiblingChildren = previousSibling.children!;
            return previousSiblingChildren[previousSiblingChildren.length - 1];
          }
          return previousSiblingId;
        }
      }

      return currentNode.parentId || null;
    },
    [canDescendIntoChildren, getNodeData]
  );

  const isFocusInInputOrDetailArea = useCallback(() => {
    const activeElement = document.activeElement;
    if (!activeElement) return false;

    const tagName = activeElement.tagName?.toLowerCase();
    if (
      activeElement instanceof HTMLInputElement ||
      activeElement instanceof HTMLTextAreaElement ||
      activeElement instanceof HTMLSelectElement ||
      tagName === 'input' ||
      tagName === 'textarea' ||
      tagName === 'select' ||
      (activeElement as HTMLElement).contentEditable === 'true'
    ) {
      return true;
    }

    const detailArea = activeElement.closest('[data-testid="detail-area"], .detail-area, [role="tabpanel"]');
    if (detailArea) {
      return true;
    }

    const muiInput = activeElement.closest(
      '.MuiInputBase-root, .MuiAutocomplete-root, .MuiSelect-root, .MuiMenu-root, [role="listbox"], [role="button"]'
    );
    if (muiInput) {
      return true;
    }

    return false;
  }, []);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!selectedNodeId) return;

      const currentNode = getNodeData(selectedNodeId);
      if (!currentNode) return;

      if (isFocusInInputOrDetailArea()) {
        return;
      }

      switch (event.key) {
        case 'ArrowDown': {
          const nextNode = getNextNode(selectedNodeId);
          if (nextNode) {
            event.preventDefault();
            onSelect(nextNode);
          }
          break;
        }

        case 'ArrowUp': {
          const previousNode = getPreviousNode(selectedNodeId);
          if (previousNode) {
            event.preventDefault();
            onSelect(previousNode);
          }
          break;
        }

        case 'ArrowRight': {
          if (!currentNode.isExpandable) {
            break;
          }
          event.preventDefault();
          if (!isNodeExpanded(selectedNodeId)) {
            onExpand(selectedNodeId);
            break;
          }
          const firstChild = currentNode.children?.[0];
          if (firstChild) {
            onSelect(firstChild);
          }
          break;
        }

        case 'ArrowLeft':
          event.preventDefault();
          if (isNodeExpanded(selectedNodeId) && currentNode.isExpandable) {
            onCollapse(selectedNodeId);
          } else if (currentNode.parentId) {
            onSelect(currentNode.parentId);
          }
          break;

        case 'Enter':
          if (currentNode.isExpandable) {
            event.preventDefault();
            if (isNodeExpanded(selectedNodeId)) {
              onCollapse(selectedNodeId);
            } else {
              onExpand(selectedNodeId);
            }
          }
          break;

        case 'e':
          if ((event.ctrlKey || event.metaKey) && onEdit) {
            event.preventDefault();
            onEdit(selectedNodeId);
          }
          break;

        case 'F6':
          if (onReload) {
            event.preventDefault();
            onReload();
          }
          break;

        default:
          if (onAdd && isPlusKey(event) && !event.altKey) {
            event.preventDefault();
            onAdd();
          }
          break;
      }
    },
    [
      selectedNodeId,
      getNodeData,
      isNodeExpanded,
      getNextNode,
      getPreviousNode,
      onSelect,
      onExpand,
      onCollapse,
      onEdit,
      onReload,
      onAdd,
      isFocusInInputOrDetailArea,
    ]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);
};
